import { useMutation } from '@apollo/client';
import { EmailTemplateEditor } from '@nile/banyan/src/Components/Editors';
import {
  TEMPLATE_SUB_TYPES,
  TEMPLATE_TYPES
} from '@nile/banyan/src/Constants/notifications';
import { CREATE_NOTIFICATION_TEMPLATE } from '@nile/banyan/src/Graphql/Mutations/templates';
import {
  AIComponent,
  ActionGroup,
  emailTemplateConstarints,
  getAIRephraseKeys,
  getLokaliseKeyByProduct,
  getTranslatedText,
  preFormatForm,
  toast,
  useProduct,
  validateForm
} from '@nile/components';
import {
  EventEmitter,
  dompurifier,
  getAIPrompts,
  formatContentWithMentionsAndPlaceholders,
  restructureHtml
} from '@nile/components/src/Utils/helpers';
import { APPLY_AI_PROMPT, AccountClient } from '@nile/graphql';
import {
  Page,
  TextContainer,
  TextStyle,
  ToggleButton
} from '@superops/ui-design-system';
import dompurify from 'dompurify';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { cloneDeep } from 'lodash';
import React, { Component, useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';

const Wrapper = styled.div`
  width: ${(props) => (props.isPopup ? '80vw' : 'inherit')};

  .template-left-panel {
    overflow-y: auto;
    margin-right: 10px;
    width: 75%;
    padding: 2px;
    /* height: 68%; */
  }
  .template-right-panel {
    /* height: 68%; */
    display: inline-flex;
    width: 24%;
  }
  .template-name {
    width: 450px;
  }

  .btn-split {
    align-items: center;
  }
`;

function NewReplyContainer(props) {
  const {
    intl,
    template,
    meta,
    onCancel,
    onSuccess,
    type,
    updateTemplate,
    isPopup,
    module,
    enableDefault
  } = props;

  const flags = useFlags();
  const currentProduct = useProduct();

  const [submitActionLoading, setSubmitActionLoading] = useState(false);
  const [errors, setErrors] = useState(null);

  const editorRef = React.useRef(null);

  const AIPrompts = getAIPrompts();

  const [createReplyTemplate] = useMutation(CREATE_NOTIFICATION_TEMPLATE, {
    client: AccountClient,
    onCompleted({ createNotificationTemplate }) {
      /* Close sheet container once an employee created */
      if (createNotificationTemplate) {
        // toggleActive()
        if (props?.type === TEMPLATE_TYPES.sideConversation) {
          EventEmitter.dispatch('sideConversationTemplateCreated', {
            response: createNotificationTemplate
          });
        }
        toast('success', 'toast.admin.replytemplate.create.success');
        window.setTimeout(() => {
          props.push('/onboard/templates');
        }, 1000);
      }
    },
    onError({ graphQLErrors }) {}
  });

  function saveContent() {
    let {
      content,
      name,
      placeholdersUsed,
      to,
      cc,
      bcc,
      subject,
      // senderNameType,
      // senderNameValue,
      // emailSignatureType,
      attachmentList,
      templateSubtype,
      defaultTemplate
    } = template;

    let sanatizeTags = /<[^>]*>?/gm;
    content = formatContentWithMentionsAndPlaceholders(content);
    content = restructureHtml(content);

    //Validation object for email template
    let validationObj = {
      content: content.replace(sanatizeTags, ''),
      subject,
      name
    };
    let validationContraints = cloneDeep(emailTemplateConstarints);
    //For reply templates we dont have subject field, so deleting it before initialization
    if (props.type === TEMPLATE_TYPES.reply) {
      delete validationObj.subject;
      delete validationContraints.subject;
    }

    //For custom ticketing templates we dont need name field, so deleting it before initialization
    if (
      props.type === TEMPLATE_TYPES.customTicketing ||
      props.type === TEMPLATE_TYPES.invoice
    ) {
      delete validationObj.name;
      delete validationContraints.name;
    }

    let validationResult = validateForm(
      preFormatForm(validationObj),
      validationContraints
    );

    if (validationResult) {
      setErrors({
        name:
          validationResult.name && validationResult.name.length
            ? validationResult.name[0]
            : '',
        content:
          validationResult.content && validationResult.content.length
            ? validationResult.content[0]
            : '',
        subject:
          validationResult.subject && validationResult.subject.length
            ? validationResult.subject[0]
            : ''
      });
    } else {
      setSubmitActionLoading(true);
      if (onSuccess) {
        onSuccess({
          content: dompurifier(content),
          name,
          placeholdersUsed,
          subject,
          to,
          cc,
          bcc,
          templateSubtype,
          defaultTemplate,
          attachmentList
        }).then((res) => setSubmitActionLoading(false));
      } else {
        createReplyTemplate({
          variables: {
            notificationTemplateType:
              props.type === TEMPLATE_TYPES.sideConversation
                ? TEMPLATE_TYPES.sideConversation
                : TEMPLATE_TYPES.reply,
            content: dompurifier(content),
            subject,
            name,
            placeholdersUsed,
            to,
            cc,
            bcc,
            templateSubtype,
            defaultTemplate,
            attachmentList
          },
          input: {
            notificationTemplateType:
              props.type === TEMPLATE_TYPES.sideConversation
                ? TEMPLATE_TYPES.sideConversation
                : TEMPLATE_TYPES.reply,
            content: dompurifier(content),
            name,
            subject,
            placeholdersUsed,
            to,
            cc,
            bcc,
            templateSubtype,
            defaultTemplate,
            attachmentList
          }
        }).then((res) => setSubmitActionLoading(false));
      }
    }
  }

  function updateName(val) {
    props.updateTemplateField('name', val);
  }

  function backToPage() {
    props.push('/onboard/templates');
  }

  const aiRephraseKeys = getAIRephraseKeys(intl);

  function templateOnChange(obj) {
    if (errors) {
      setErrors(null);
    }
    updateTemplate(obj);
  }

  const onRephraseCopy = (content, params) => {
    EventEmitter.dispatch('insertEditorContent', dompurifier(content));
  };

  return (
    <Wrapper className="full-height white-grey-page" isPopup={isPopup}>
      <div
        className={`full-height ${isPopup || props?.noPadding ? '' : 'p20'}`}
      >
        <Page
          variant="primary"
          contentHeight="120px"
          subtitle={
            props.type === TEMPLATE_TYPES.invoice ? null : (
              <TextContainer>
                <TextStyle variation="subdued">
                  <FormattedMessage
                    id={getLokaliseKeyByProduct(
                      currentProduct,
                      'replyContainerDesc'
                    )}
                  />
                </TextStyle>
              </TextContainer>
            )
          }
          title={
            <div className="flex actr g10">
              <div>
                <TextStyle element="h1" weight="Bold">
                  <FormattedMessage
                    id={
                      props.type === TEMPLATE_TYPES.invoice
                        ? 'invoice.reminder.templateHeader'
                        : 'admin.eventTrigger.customNotif.header'
                    }
                  />
                </TextStyle>
              </div>

              {props?.type === TEMPLATE_TYPES.reply && (
                <div className="flex">
                  <AIComponent
                    editorRef={editorRef}
                    onCopy={onRephraseCopy}
                    type="REPHRASE"
                    prompts={AIPrompts || []}
                    data={{
                      Api: APPLY_AI_PROMPT,
                      accessorKey: 'applyAIPrompt'
                    }}
                    buttonText={getTranslatedText(
                      intl,
                      'settings.runbook.ai.rephrase.text'
                    )}
                    featureName="PROMPT_BUILDER"
                    headingText={getTranslatedText(
                      intl,
                      'common.rephrased.text'
                    )}
                    headingIcon={'AIWand'}
                    loadingText={aiRephraseKeys?.loadingText}
                    successText={aiRephraseKeys?.successText}
                    copyButtonText={getTranslatedText(
                      intl,
                      'common.insert.text'
                    )}
                    copySuccessText={getTranslatedText(intl, 'common.inserted')}
                    mixpanelEventOnClick={{
                      event: 'Rephrased with prompt - New reply template editor'
                    }}
                    mixpanelEventOnCopy={{
                      event:
                        'Copy and pasted rephrased content - New reply template'
                    }}
                  />
                </div>
              )}
            </div>
          }
          primaryAction={
            <div className="flex center">
              {enableDefault && (
                <div className="mlauto flex center mr-10">
                  <ToggleButton
                    value={template?.defaultTemplate}
                    onChange={(val) =>
                      templateOnChange({ defaultTemplate: val })
                    }
                    tippy={{
                      position: 'top'
                    }}
                    size="small"
                  />
                  <div className="ml8">
                    <FormattedMessage id="admin.replyTemplate.markAsDefault" />
                  </div>
                </div>
              )}

              <ActionGroup
                secondaryAction={onCancel || backToPage}
                primaryAction={saveContent}
                primaryLoading={submitActionLoading}
              />
            </div>
          }
        >
          <EmailTemplateEditor
            template={template}
            validationError={errors}
            updateTemplate={templateOnChange}
            allowAttachments={props.type !== TEMPLATE_TYPES.invoice}
            isNotification={!!onCancel}
            type={type}
            module={module || 'REPLY_TEMPLATE'}
            details={{ operation: 'CREATE' }}
            hideSubject={props?.type === TEMPLATE_TYPES.reply}
            errors={errors}
            updateName={updateName}
            editorRef={editorRef}
          />
        </Page>
      </div>
    </Wrapper>
  );
}

class NewReplyEditorComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      template: {
        activated: props?.template?.activated || true,
        attachmentList: props?.template?.attachmentList || [],
        subject: props?.template?.subject || '',
        name: props?.template?.name || '',
        content: dompurifier(props?.template?.content) || '',
        placeholdersUsed: props?.template?.placeholdersUsed || '',
        templateType:
          props?.type || props?.template?.templateType || TEMPLATE_TYPES.reply,
        templateSubtype:
          props?.template?.templateSubtype || TEMPLATE_SUB_TYPES.technician,
        to: props?.template?.to || [],
        bcc: props?.template?.bcc || [],
        cc: props?.template?.cc || [],
        id: props?.template?.id || null,
        // senderNameType: props?.template?.senderNameType || 'MSP',
        // senderNameValue: props?.template?.senderNameValue || null,
        // emailSignatureType: props?.template?.emailSignatureType || 'MSP',
        canConfigureTo: props?.template?.canConfigureTo || true,
        canConfigureCcAndBcc: props?.template?.canConfigureCcAndBcc || true
      },
      meta: {
        name: 'New template',
        description: ''
      }
    };
  }

  handleTemplateUpdation = (obj) => {
    const updatedTemplate = { ...cloneDeep(this.state.template), ...obj };
    this.setState({ template: updatedTemplate });
  };

  updateTemplateField = (field, value) => {
    this.setState({
      template: {
        ...this.state.template,
        [field]: value
      }
    });
  };

  render() {
    const { template, meta, placeholdersUsed } = this.state;
    const {
      intl,
      history,
      onCancel,
      onSuccess,
      type,
      isPopup,
      module,
      enableDefault,
      noPadding
    } = this.props;

    return (
      <NewReplyContainer
        intl={intl}
        template={template}
        updateTemplate={this.handleTemplateUpdation}
        updateTemplateField={this.updateTemplateField}
        push={history.push}
        meta={meta}
        placeholdersUsed={placeholdersUsed}
        onCancel={onCancel}
        onSuccess={onSuccess}
        type={type}
        isPopup={isPopup}
        module={module}
        enableDefault={enableDefault}
        noPadding={noPadding}
      />
    );
  }
}

export default injectIntl(withRouter(NewReplyEditorComponent));
